/**
 * The shout button and the sheet behind it.
 *
 * The button is on every screen, which is the whole idea: the thought that is
 * worth shouting arrives while you are looking at a score, not while you are
 * looking at a compose screen. It sits above the bottom bar on the left; the
 * right corner belongs to the overflow menu.
 *
 * Its ground is `--fg` — literally the opposite end of the palette from the
 * page's `--bg`, which is what makes it visible on all thirty-two teams without
 * a per-team exception. The glyph is an emoji, and emoji ignore `color`, so the
 * contrast has to come from the ground rather than from the mark.
 *
 * The sheet is the composer. Three things in it are less obvious than they look:
 *
 *   - the week picker. A shout is filed against a week, defaulting to the next
 *     one that is actually on, and you may post forward but never back. People
 *     do talk about next Sunday before it happens; nobody needs to edit a week
 *     that is already over.
 *
 *   - the @ menu. It opens on the `@` keystroke and filters as you type, and
 *     the list is the people who have been here — the same directory Online
 *     draws from, so somebody who opted out is not in it.
 *
 *   - the image. One, up to 10 MB, JPG/PNG/GIF. Anything over a megabyte is
 *     also re-drawn smaller on a canvas and uploaded alongside the original;
 *     the feed renders the small one and offers the full file as a download.
 *     The client's checks exist for a fast, legible failure — the Worker
 *     repeats all of them against the actual bytes, because a client-side
 *     check is a courtesy and never a control.
 */

import {
  useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Shout, WeekRef } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET, postForm } from '../lib/api'
import { weekLabelWithRange } from '../lib/dates'
import { Button, Notice, Sheet, Spacer } from './index'

const MAX_BODY = 500
const MAX_IMAGE_BYTES = 10 * 1024 * 1024
/** Above this, the feed gets a smaller copy and the original a download link. */
const DISPLAY_THRESHOLD = 1024 * 1024
/** Longest edge of the display copy. 1600 is retina-sharp at any phone width. */
const DISPLAY_MAX_EDGE = 1600
const OK_TYPES = ['image/jpeg', 'image/png', 'image/gif']

/**
 * Draw the picture smaller, in the browser, and hand both files to the server.
 *
 * A Worker has no image decoder, so the alternatives were a paid transform
 * binding or making everybody download an 8 MB photo to read a one-line shout.
 * A canvas is neither. Returns null when there is nothing worth doing:
 *
 *   - **GIFs, always.** Drawing one to a canvas yields a still of frame one.
 *     A silently un-animated GIF is worse than a big download.
 *   - **Anything already under the threshold**, which needs no second copy.
 *   - **A re-encode that came out no smaller**, which happens with small
 *     dimensions at high bit depth and would double the storage for nothing.
 *
 * JPEG at 0.82 in every case, including for a PNG source: a PNG photo is the
 * thing this exists for, and re-encoding it as PNG saves almost nothing.
 */
async function makeDisplayCopy(file: File): Promise<File | null> {
  if (file.type === 'image/gif' || file.size <= DISPLAY_THRESHOLD) return null

  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('decode failed'))
      el.src = url
    })

    const longest = Math.max(img.naturalWidth, img.naturalHeight)
    const scale = longest > DISPLAY_MAX_EDGE ? DISPLAY_MAX_EDGE / longest : 1
    const w = Math.max(1, Math.round(img.naturalWidth * scale))
    const h = Math.max(1, Math.round(img.naturalHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, w, h)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.82)
    })
    if (!blob || blob.size >= file.size) return null

    return new File([blob], 'display.jpg', { type: 'image/jpeg' })
  } catch {
    // No display copy is a fine outcome: the original still posts.
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

interface Person { id: string; displayName: string }

/** Screens that own the whole viewport and have no bottom bar. */
const HIDE_ON = ['/join', '/signin', '/verify', '/setup', '/claim-name', '/terms', '/privacy', '/admin']

// ── the button ──────────────────────────────────────────────────────────────

export function ShoutButton() {
  const { state } = useStore()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  if (!state || state.user.optedOut) return null
  if (HIDE_ON.some((p) => pathname.startsWith(p))) return null

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Shout"
        title="Shout"
        style={{
          position: 'fixed',
          // Left, not right: the right corner is where the ⋯ menu opens from,
          // and two floating things stacking in one corner is one too many.
          left: 14,
          bottom: `calc(14px + var(--nav-h))`,
          zIndex: 40,
          width: 48,
          height: 48,
          borderRadius: 16,
          // The opposite end of the palette from the page ground.
          background: 'var(--fg)',
          border: '1px solid var(--bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          lineHeight: 1,
          boxShadow: '0 6px 20px rgba(0,0,0,.35)',
        }}
      >
        <span aria-hidden>📣</span>
      </button>
      <ShoutSheet open={open} onClose={() => setOpen(false)} />
    </>
  )
}

// ── the composer ────────────────────────────────────────────────────────────

export function ShoutSheet({
  open, onClose, defaultWeekId, onPosted,
}: {
  open: boolean
  onClose: () => void
  /** Overrides the default week — the Shouts page passes whatever it's showing. */
  defaultWeekId?: string
  /** When set, the sheet hands the new shout back instead of navigating. */
  onPosted?: (shout: Shout) => void
}) {
  const { state, refresh } = useStore()
  const navigate = useNavigate()

  const [weeks, setWeeks] = useState<WeekRef[] | null>(null)
  const [weekId, setWeekId] = useState<string>('')
  const [body, setBody] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [people, setPeople] = useState<Person[]>([])
  const [mentionAt, setMentionAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const textRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // Reset every time it opens: a half-written shout from twenty minutes ago is
  // not a draft anyone wants restored.
  useEffect(() => {
    if (!open) return
    setBody('')
    setFile(null)
    setErr(null)
    setMentionAt(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    const ac = new AbortController()
    void (async () => {
      try {
        const res = await GET<{ weeks: WeekRef[]; currentWeekId: string | null }>(
          `/api/weeks${state?.week ? `?season=${state.week.seasonYear}` : ''}`, ac.signal,
        )
        setWeeks(res.weeks)
      } catch { /* the select falls back to the current week alone */ }
      try {
        const res = await GET<{ users: Person[] }>('/api/users', ac.signal)
        setPeople(res.users)
      } catch { /* the @ menu just won't offer anything */ }
    })()
    return () => ac.abort()
  }, [open, state?.week])

  // Postable weeks: on, and not already over. Sorted forward, because the
  // choice is "this Sunday or a later one".
  const postable = useMemo(() => {
    const now = Date.now()
    const all = weeks ?? (state?.week ? [state.week] : [])
    return all
      .filter((w) => w.isEnabled && w.endsAt >= now)
      .sort((a, b) => a.startsAt - b.startsAt)
  }, [weeks, state?.week])

  useEffect(() => {
    if (!open) return
    const wanted = defaultWeekId && postable.some((w) => w.id === defaultWeekId)
      ? defaultWeekId
      : postable[0]?.id ?? ''
    setWeekId(wanted)
  }, [open, defaultWeekId, postable])

  // ── the image ──

  useEffect(() => {
    if (!file) { setPreview(null); return }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const onPickFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null
    e.target.value = '' // so picking the same file twice still fires
    if (!picked) return
    if (!OK_TYPES.includes(picked.type)) {
      setErr('Images have to be a JPG, PNG or GIF.')
      return
    }
    if (picked.size > MAX_IMAGE_BYTES) {
      setErr('That image is over 10 MB. Pick a smaller one.')
      return
    }
    setErr(null)
    setFile(picked)
  }, [])

  // ── the @ menu ──

  const onBodyChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value.slice(0, MAX_BODY)
    setBody(next)
    const caret = e.target.selectionStart ?? next.length
    // Walk back from the caret to the nearest `@`, giving up at whitespace: the
    // menu belongs to the token being typed and nothing earlier.
    let i = caret - 1
    while (i >= 0 && !/\s/.test(next[i]) && next[i] !== '@') i--
    setMentionAt(i >= 0 && next[i] === '@' ? i : null)
  }, [])

  const mentionQuery = mentionAt === null ? '' : body.slice(mentionAt + 1).toLowerCase()
  const mentionMatches = mentionAt === null
    ? []
    : people
      .filter((p) => p.displayName.toLowerCase().startsWith(mentionQuery))
      .slice(0, 6)

  const insertMention = useCallback((person: Person) => {
    if (mentionAt === null) return
    const before = body.slice(0, mentionAt)
    const after = body.slice(mentionAt + 1 + mentionQuery.length)
    const next = `${before}@${person.displayName}${after.startsWith(' ') ? '' : ' '}${after}`
    setBody(next.slice(0, MAX_BODY))
    setMentionAt(null)
    textRef.current?.focus()
  }, [body, mentionAt, mentionQuery.length])

  // ── send ──

  const submit = useCallback(async () => {
    if (!weekId || !body.trim()) return
    setBusy(true)
    setErr(null)
    try {
      const form = new FormData()
      form.set('weekId', weekId)
      form.set('body', body.trim())
      if (file) {
        form.set('image', file)
        const display = await makeDisplayCopy(file)
        if (display) form.set('display', display)
      }
      const res = await postForm<{ shout: Shout }>('/api/shouts', form)
      onClose()
      void refresh(true)
      if (onPosted) onPosted(res.shout)
      else navigate(`/shouts?week=${encodeURIComponent(weekId)}`)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'That didn’t send. Try again.')
    } finally {
      setBusy(false)
    }
  }, [weekId, body, file, onClose, refresh, onPosted, navigate])

  const canSend = !busy && !!weekId && body.trim().length > 0

  const selectStyle: CSSProperties = {
    width: '100%', padding: '13px 14px', borderRadius: 12, minHeight: 48,
    border: '1px solid var(--outline)', background: 'var(--bg)', color: 'var(--fg)',
    fontSize: 14,
  }

  return (
    <Sheet open={open} onClose={onClose} labelledBy="shout-title">
      <div id="shout-title" style={{ fontSize: 18, fontWeight: 800 }}>Shout</div>

      <Spacer h={14} />
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textRef}
          value={body}
          onChange={onBodyChange}
          placeholder="We’re counting on you to heighten the intellectual discourse."
          rows={4}
          aria-label="What do you want to say?"
          style={{
            width: '100%', padding: '14px 15px', borderRadius: 12,
            border: '1px solid var(--outline)', background: 'var(--bg)', color: 'var(--fg)',
            fontSize: 15, lineHeight: 1.45, resize: 'vertical', minHeight: 96,
          }}
        />
        {mentionMatches.length ? (
          <div
            role="listbox"
            aria-label="People"
            style={{
              position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 2,
              background: 'var(--surf)', border: '1px solid var(--bright)',
              borderRadius: 12, overflow: 'hidden', marginTop: 4,
              // It floats over the week picker while it is open, so it has to
              // read as a menu rather than as the next field down.
              boxShadow: '0 10px 28px rgba(0,0,0,.55)',
            }}
          >
            {mentionMatches.map((p) => (
              <button
                key={p.id}
                role="option"
                aria-selected={false}
                onMouseDown={(e) => { e.preventDefault(); insertMention(p) }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '11px 14px', minHeight: 44, fontSize: 14, color: 'var(--fg)',
                  borderBottom: '1px solid var(--line)',
                }}
              >
                @{p.displayName}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div
        className="mono"
        style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--dim)', marginTop: 6 }}
      >
        <span>@ to mention someone</span>
        <span>{body.length}/{MAX_BODY}</span>
      </div>

      <Spacer h={14} />
      <label htmlFor="shout-week" style={{ display: 'block', fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>
        Post to
      </label>
      <select
        id="shout-week"
        value={weekId}
        onChange={(e) => setWeekId(e.target.value)}
        style={selectStyle}
      >
        {postable.length === 0 ? <option value="">No open weeks</option> : null}
        {postable.map((w) => (
          <option key={w.id} value={w.id}>
            {weekLabelWithRange(w.label, w.startsAt, w.endsAt)}
          </option>
        ))}
      </select>

      <Spacer h={14} />
      {preview ? (
        <div style={{ position: 'relative' }}>
          <img
            src={preview}
            alt="Attached"
            style={{
              width: '100%', maxHeight: 300, objectFit: 'contain',
              background: 'var(--bg)', borderRadius: 12,
              border: '1px solid var(--line)', display: 'block',
            }}
          />
          <button
            onClick={() => setFile(null)}
            aria-label="Remove image"
            style={{
              position: 'absolute', top: 8, right: 8, width: 36, height: 36, borderRadius: 999,
              background: 'var(--bg)', border: '1px solid var(--outline)', color: 'var(--fg)',
              fontSize: 15, fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            width: '100%', padding: 13, minHeight: 48, borderRadius: 12,
            border: '1px dashed var(--outline)', color: 'var(--fg)', fontSize: 14, fontWeight: 600,
          }}
        >
          Add a picture
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/gif"
        onChange={onPickFile}
        style={{ display: 'none' }}
      />

      {err ? <><Spacer h={14} /><Notice tone="alert">{err}</Notice></> : null}

      <Spacer h={18} />
      <Button disabled={!canSend} onClick={() => { void submit() }}>
        {busy ? 'Sending…' : 'Shout'}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Cancel</Button>
    </Sheet>
  )
}
